import List from '@/layouts/default/List.vue'
import CommandStatus from '@/models/enums/kkm-server/CommandStatus'
import axios from 'axios'
import store from '@/store'

export const DEFAULT_KKMSERVER_URL =
  process.env.VUE_APP_KKMSERVER_URL ?? 'https://kkmserver:5893'

class KkmServerApiClient {
  isExtensionEnabled () {
    return window.KkmServer !== undefined
  }

  // IdCommand для разовых команд клиента; команды конвейера приходят с id от бэка
  generateGuid () {
    function S4 () {
      return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1)
    }
    return (
      S4() +
      S4() +
      '-' +
      S4() +
      '-' +
      S4() +
      '-' +
      S4() +
      '-' +
      S4() +
      S4() +
      S4()
    )
  }

  async executeCommand (command) {
    const result = await this.sendCommand(command)
    console.log('----Result----', command.IdCommand, result)
    if (result.Status === 5) {
      return this.checkCommandStatus(command.IdCommand)
    } else {
      return Promise.resolve(result)
    }
  }

  _withTimeout (promise, timeoutMs, label) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(
        () => reject(new Error(`KkmServer timeout (${label}, ${timeoutMs}ms)`)),
        timeoutMs,
      )
      promise.then(
        value => { clearTimeout(timer); resolve(value) },
        error => { clearTimeout(timer); reject(error) },
      )
    })
  }

  sendCommand (command, timeoutMs = 15000) {
    const raw = new Promise((resolve, reject) => {
      if (this.isExtensionEnabled()) {
        console.log('Sending command:', command)
        window.KkmServer.Execute(resolve, command)
      } else {
        this.sendCommandByHttp(command, resolve, reject, timeoutMs)
      }
    })
    // Зависший KkmServer с открытым портом не должен вешать конвейер навсегда
    return this._withTimeout(raw, timeoutMs, command.Command)
  }

  // Проба доступности перед Execute: List без побочных эффектов. Провал пробы
  // означает «Execute не вызывался» - только такие ошибки можно ретраить.
  async probe () {
    const result = await this.sendCommand({ Command: 'List' }, 3000)
    if (result.Status !== 0) {
      throw new Error('KkmServer probe failed: Status ' + result.Status)
    }
  }

  baseUrl () {
    return store.getters['printServer/getKkmServerUrl'] || DEFAULT_KKMSERVER_URL
  }

  // Пульс для вочдога: GET /VersionJSON - штатный маршрут KkmServer до авторизации, без команды,
  // очереди устройств и журнала; простой GET без preflight. Ответ { Version }. Без логов в консоль:
  // зовётся каждые 30 с
  async getVersion (timeoutMs = 5000) {
    const response = await axios.get(this.baseUrl() + '/VersionJSON', { timeout: timeoutMs })
    return response.data && response.data.Version ? String(response.data.Version) : null
  }

  sendCommandByHttp (command, onSuccess, onError, timeoutMs = 15000) {
    const url = this.baseUrl() + '/Execute'

    console.log('Sending command by http:', url, command)

    axios
      .post(url, command, { timeout: timeoutMs })
      .then((res) => onSuccess(res.data))
      .catch((e) => onError(e))
  }

  async checkCommandStatus (commandId) {
    return new Promise((resolve, reject) => {
      let step = 1
      const timer = setInterval(async () => {
        console.log(`checkCommandStatus commandId=${commandId} step=${step}`)
        if (step <= 5) {
          step += 1
          try {
            const result = await this.getCommandStatus(commandId)
            console.log('----check Result----', commandId, result)
            if (result.Status !== 5) {
              clearInterval(timer)
              resolve(result)
            }
          } catch (e) {
            clearInterval(timer)
            reject(new Error(`Не удалось проверить статус команды: ${e}`))
          }
        } else {
          clearInterval(timer)
          reject(
            new Error(
              `Не удалось проверить статус команды за ${step - 1} шагов`,
            ),
          )
        }
      }, 2000)
    })
  }

  getCommandStatus (commandId) {
    if (!commandId) {
      return Promise.reject(
        new Error('Empty commandId while check command status'),
      )
    }
    const command = {
      Command: 'GetRezult',
      IdCommand: commandId,
    }

    return this.sendCommand(command).then((response) => {
      return response.Rezult
    })
  }

  async getCommandResult (commandId, callback) {
    if (!commandId) {
      return Promise.reject(new Error('Empty commandId'))
    }

    const data = { Command: 'GetRezult', IdCommand: commandId }

    function setResult (result) {
      if (
        result.Status === CommandStatus.Run ||
        result.Status === CommandStatus.NotRun
      ) {
        setTimeout(() => {
          kkmServerApiClient.sendCommand(data).then(setResult)
        }, 1000)
      } else {
        callback(result)
      }
    }

    try {
      setResult(await this.sendCommand(data))
    } catch (e) {
      return Promise.reject(e)
    }
  }

  async getUnitsList () {
    try {
      const data = {
        Command: 'List',
      }
      const result = await this.sendCommand(data)
      if (result.Status === 0) {
        return Promise.resolve(result.ListUnit)
      } else {
        return Promise.reject(result.Error)
      }
    } catch (e) {
      return Promise.reject(e)
    }
  }

  // Состояние ККТ: KkmServer перед ответом перечитывает устройство (СНО, ФФД, ФН).
  async getDataKkt (numDevice) {
    const result = await this.sendCommand({
      IdCommand: this.generateGuid(),
      Command: 'GetDataKKT',
      NumDevice: numDevice,
    })

    if (result.Status !== 0 || !result.Info) {
      throw new Error(result.Error || `GetDataKKT: Status ${result.Status}`)
    }

    return result.Info
  }
}

const kkmServerApiClient = new KkmServerApiClient()

export default kkmServerApiClient
